/**
 * SC-06: 営業予実ダッシュボード
 *
 * 営業担当者の予実管理画面（評価制度規程準拠）
 * - 担当者予実: 目標設定と実績管理
 * - 推移分析: 週別/月別/四半期別の達成率推移
 *
 * @created 2025-11-16
 * @updated 2025-12-17 評価制度規程準拠の予実管理機能に改修
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Grid,
  LinearProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Paper,
} from '@mui/material';
import {
  ArrowBack,
  GetApp,
  TrendingUp,
  TrendingDown,
  Add as AddIcon,
  Edit as EditIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import {
  salesKpiApi,
  salesExportApi,
  EmployeeTarget,
  EmployeePerformanceItem,
  EmployeeTrendData,
  PhaseContributionDetail,
  salesApprovalsApi,
} from '../../services/salesApi';
import ApprovalWorkflowTab from '../../components/Sales/ApprovalWorkflowTab';
import api from '../../services/api';
import { formatCurrencyWithUnit } from '../../utils/formatNumber';
import { extractErrorMessage } from '../../utils/errorHandler';
import { formatDateJST } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

interface Employee {
  employee_id: string;
  name: string;
  department_name?: string;
}

const SalesKpiDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { salesPermissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [fiscalYear, setFiscalYear] = useState(new Date().getFullYear());

  // 年度選択肢（現在年度 -1年 〜 +2年）
  const currentYear = new Date().getFullYear();
  const fiscalYears = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

  // 担当者予実
  const [targets, setTargets] = useState<EmployeeTarget[]>([]);
  const [performance, setPerformance] = useState<EmployeePerformanceItem[]>([]);
  const [performanceSummary, setPerformanceSummary] = useState<{
    total_target_expected_sales: number;
    total_actual_expected_sales: number;
    expected_sales_achievement_rate: number;
    total_target_gross_profit: number;
    total_actual_gross_profit: number;
    gross_profit_achievement_rate: number;
  } | null>(null);

  // 推移分析
  const [trendPeriod, setTrendPeriod] = useState<'week' | 'month' | 'quarter'>('week');
  const [trendData, setTrendData] = useState<EmployeeTrendData[]>([]);
  const [trendPeriods, setTrendPeriods] = useState<string[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');

  // 目標設定ダイアログ
  const [targetDialogOpen, setTargetDialogOpen] = useState(false);
  const [targetFiscalYear, setTargetFiscalYear] = useState(new Date().getFullYear());
  const [editingTarget, setEditingTarget] = useState<{
    employee_id: string;
    employee_name: string;
    target_expected_sales: number;
    target_gross_profit: number;
    notes: string;
  } | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [savingTarget, setSavingTarget] = useState(false);

  // 内訳ダイアログ
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [breakdownEmployee, setBreakdownEmployee] = useState<EmployeePerformanceItem | null>(null);
  const [breakdownDetails, setBreakdownDetails] = useState<PhaseContributionDetail[]>([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  // データ取得
  const fetchPerformanceData = useCallback(async () => {
    try {
      setLoading(true);
      const [targetsRes, perfRes] = await Promise.all([
        salesKpiApi.getEmployeeTargets(fiscalYear),
        salesKpiApi.getEmployeePerformance(fiscalYear),
      ]);

      if (targetsRes.data.success) {
        setTargets(targetsRes.data.data.targets);
      }
      if (perfRes.data.success) {
        setPerformance(perfRes.data.data.employees);
        setPerformanceSummary(perfRes.data.data.summary);
      }
    } catch (err) {
      console.error('予実データ取得エラー:', err);
      setError(extractErrorMessage(err, '予実データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [fiscalYear]);

  const fetchTrendData = useCallback(async () => {
    try {
      setLoading(true);
      const params: { fiscal_year: number; period: 'week' | 'month' | 'quarter'; employee_id?: string } = {
        fiscal_year: fiscalYear,
        period: trendPeriod,
      };
      if (selectedEmployee) {
        params.employee_id = selectedEmployee;
      }

      const response = await salesKpiApi.getPerformanceTrend(params);

      if (response.data.success) {
        setTrendData(response.data.data.employees);
        setTrendPeriods(response.data.data.periods);
      }
    } catch (err) {
      console.error('推移データ取得エラー:', err);
      setError(extractErrorMessage(err, '推移データの取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, trendPeriod, selectedEmployee]);

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees', { params: { limit: 500 } });
      if (response.data.success && response.data.data?.data) {
        // 営業部門の社員のみ（または全員）
        setEmployees(response.data.data.data);
      }
    } catch (err) {
      console.error('社員リスト取得エラー:', err);
    }
  };

  useEffect(() => {
    if (tabValue === 1) {
      fetchPerformanceData();
    } else if (tabValue === 2) {
      fetchTrendData();
    }
  }, [tabValue, fetchPerformanceData, fetchTrendData]);

  useEffect(() => {
    fetchEmployees();
  }, []);

  // 内訳ダイアログ
  const handleOpenBreakdown = async (row: EmployeePerformanceItem) => {
    setBreakdownEmployee(row);
    setBreakdownOpen(true);
    setBreakdownLoading(true);
    setBreakdownDetails([]);
    try {
      const res = await salesKpiApi.getPhaseContributions({
        fiscal_year: fiscalYear,
        employee_id: row.employee_id,
      });
      if (res.data.success) {
        setBreakdownDetails(res.data.data.details);
      }
    } catch (err) {
      console.error('内訳取得エラー:', err);
    } finally {
      setBreakdownLoading(false);
    }
  };

  // 目標設定ダイアログ
  const handleOpenTargetDialog = (employee?: EmployeePerformanceItem) => {
    setTargetFiscalYear(fiscalYear); // ダッシュボードの年度で初期化
    if (employee) {
      const existingTarget = targets.find(t => t.employee_id === employee.employee_id);
      setEditingTarget({
        employee_id: employee.employee_id,
        employee_name: employee.employee_name,
        target_expected_sales: existingTarget?.target_expected_sales || employee.target_expected_sales || 0,
        target_gross_profit: existingTarget?.target_gross_profit || employee.target_gross_profit || 0,
        notes: existingTarget?.notes || '',
      });
    } else {
      setEditingTarget({
        employee_id: '',
        employee_name: '',
        target_expected_sales: 0,
        target_gross_profit: 0,
        notes: '',
      });
    }
    setTargetDialogOpen(true);
  };

  const handleSaveTarget = async () => {
    if (!editingTarget || !editingTarget.employee_id) {
      setError('担当者を選択してください');
      return;
    }

    try {
      setSavingTarget(true);
      const response = await salesKpiApi.setEmployeeTarget({
        fiscal_year: targetFiscalYear,
        employee_id: editingTarget.employee_id,
        target_expected_sales: editingTarget.target_expected_sales,
        target_gross_profit: editingTarget.target_gross_profit,
        notes: editingTarget.notes,
      });

      if (response.data.success) {
        setTargetDialogOpen(false);
        fetchPerformanceData();
      }
    } catch (err) {
      console.error('目標設定エラー:', err);
      setError(extractErrorMessage(err, '目標の設定に失敗しました'));
    } finally {
      setSavingTarget(false);
    }
  };

  // Excel出力
  const handleExport = async () => {
    try {
      const blob = await salesExportApi.exportExcel({
        year: fiscalYear,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales_performance_${fiscalYear}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel出力エラー:', err);
      setError(extractErrorMessage(err, 'Excel出力に失敗しました'));
    }
  };

  // 達成率に応じた色を返す
  const getAchievementColor = (rate: number): 'success' | 'warning' | 'error' | 'default' => {
    if (rate >= 100) return 'success';
    if (rate >= 80) return 'warning';
    return 'error';
  };

  // 推移グラフ用データを整形
  const getChartData = () => {
    if (trendData.length === 0) return [];

    // 全期間のデータを集計
    const chartData: { period: string; [key: string]: number | string }[] = [];

    for (const period of trendPeriods) {
      const dataPoint: { period: string; [key: string]: number | string } = { period };

      for (const emp of trendData) {
        const trendItem = emp.trend.find(t => t.period === period);
        if (trendItem) {
          dataPoint[`${emp.employee_name}_rate`] = trendItem.achievement_rate;
          dataPoint[`${emp.employee_name}_cum`] = trendItem.cumulative_contribution;
        }
      }

      chartData.push(dataPoint);
    }

    return chartData;
  };

  // 色のパレット
  const colors = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#0088fe', '#00c49f'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sales/dashboard')}>
          ダッシュボードに戻る
        </Button>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {fiscalYears.map((year) => (
              <Chip
                key={year}
                label={`${year}年度`}
                color={fiscalYear === year ? 'primary' : 'default'}
                variant={fiscalYear === year ? 'filled' : 'outlined'}
                onClick={() => setFiscalYear(year)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
          <Button variant="outlined" startIcon={<GetApp />} onClick={handleExport}>
            レポート出力
          </Button>
        </Box>
      </Box>

      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        営業予実ダッシュボード
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
            <Tab label="承認ワークフロー" />
            <Tab label="担当者予実" />
            <Tab label="推移分析" />
          </Tabs>
        </Box>

        <CardContent>
          {/* 承認ワークフロータブ */}
          <TabPanel value={tabValue} index={0}>
            <ApprovalWorkflowTab onRefresh={() => {
              fetchPerformanceData();
            }} />
          </TabPanel>

          {/* 担当者予実タブ */}
          <TabPanel value={tabValue} index={1}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* 全社サマリー */}
                {performanceSummary && (
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            期待売上達成率（全社）
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography
                              variant="h4"
                              color={performanceSummary.expected_sales_achievement_rate >= 100 ? 'success.main' : 'warning.main'}
                            >
                              {performanceSummary.expected_sales_achievement_rate}%
                            </Typography>
                            {performanceSummary.expected_sales_achievement_rate >= 100 ? (
                              <TrendingUp color="success" />
                            ) : (
                              <TrendingDown color="warning" />
                            )}
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(performanceSummary.expected_sales_achievement_rate, 100)}
                            sx={{ mt: 1 }}
                            color={performanceSummary.expected_sales_achievement_rate >= 100 ? 'success' : 'warning'}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              目標: {formatCurrencyWithUnit(performanceSummary.total_target_expected_sales)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              実績: {formatCurrencyWithUnit(performanceSummary.total_actual_expected_sales)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Card variant="outlined">
                        <CardContent>
                          <Typography variant="subtitle2" color="text.secondary">
                            受注粗利達成率（全社）
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography
                              variant="h4"
                              color={performanceSummary.gross_profit_achievement_rate >= 100 ? 'success.main' : 'warning.main'}
                            >
                              {performanceSummary.gross_profit_achievement_rate}%
                            </Typography>
                            {performanceSummary.gross_profit_achievement_rate >= 100 ? (
                              <TrendingUp color="success" />
                            ) : (
                              <TrendingDown color="warning" />
                            )}
                          </Box>
                          <LinearProgress
                            variant="determinate"
                            value={Math.min(performanceSummary.gross_profit_achievement_rate, 100)}
                            sx={{ mt: 1 }}
                            color={performanceSummary.gross_profit_achievement_rate >= 100 ? 'success' : 'warning'}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="caption" color="text.secondary">
                              目標: {formatCurrencyWithUnit(performanceSummary.total_target_gross_profit)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              実績: {formatCurrencyWithUnit(performanceSummary.total_actual_gross_profit)}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                    {salesPermissions?.can_manage_accounts && (
                      <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                          <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                            <Button
                              variant="contained"
                              startIcon={<AddIcon />}
                              onClick={() => handleOpenTargetDialog()}
                              fullWidth
                            >
                              目標を設定
                            </Button>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                              担当者ごとの年度目標を設定します
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    )}
                  </Grid>
                )}

                {/* 担当者別予実テーブル */}
                <Typography variant="h6" sx={{ mb: 2 }}>担当者別予実サマリー</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>担当者</TableCell>
                        <TableCell>部署</TableCell>
                        <TableCell align="right">期待売上目標</TableCell>
                        <TableCell align="right">期待売上実績</TableCell>
                        <TableCell align="center">達成率</TableCell>
                        <TableCell align="right">粗利目標</TableCell>
                        <TableCell align="right">粗利実績</TableCell>
                        <TableCell align="center">達成率</TableCell>
                        <TableCell align="center">操作</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {performance.length > 0 ? (
                        performance.map((row) => (
                          <TableRow key={row.employee_id}>
                            <TableCell>{row.employee_name}</TableCell>
                            <TableCell>{row.department_name || '-'}</TableCell>
                            <TableCell align="right">
                              {formatCurrencyWithUnit(row.target_expected_sales)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrencyWithUnit(row.actual_expected_sales)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${row.expected_sales_achievement_rate}%`}
                                size="small"
                                color={getAchievementColor(row.expected_sales_achievement_rate)}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrencyWithUnit(row.target_gross_profit)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrencyWithUnit(row.actual_gross_profit)}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                label={`${row.gross_profit_achievement_rate}%`}
                                size="small"
                                color={getAchievementColor(row.gross_profit_achievement_rate)}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Button
                                  size="small"
                                  startIcon={<ListAltIcon />}
                                  onClick={() => handleOpenBreakdown(row)}
                                >
                                  内訳
                                </Button>
                                {salesPermissions?.can_manage_accounts && (
                                  <Button
                                    size="small"
                                    startIcon={<EditIcon />}
                                    onClick={() => handleOpenTargetDialog(row)}
                                  >
                                    編集
                                  </Button>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={9} align="center">
                            <Typography color="text.secondary">データがありません</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </TabPanel>

          {/* 推移分析タブ */}
          <TabPanel value={tabValue} index={2}>
            {/* フィルター */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>期間</InputLabel>
                <Select
                  value={trendPeriod}
                  onChange={(e) => setTrendPeriod(e.target.value as 'week' | 'month' | 'quarter')}
                  label="期間"
                >
                  <MenuItem value="week">週別</MenuItem>
                  <MenuItem value="month">月別</MenuItem>
                  <MenuItem value="quarter">四半期別</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <Autocomplete
                  size="small"
                  options={Array.isArray(employees) ? employees : []}
                  getOptionLabel={(option) => option.name || ''}
                  value={(Array.isArray(employees) ? employees.find(e => e.employee_id === selectedEmployee) : null) || null}
                  onChange={(_, newValue) => setSelectedEmployee(newValue?.employee_id || '')}
                  renderInput={(params) => <TextField {...params} label="担当者でフィルター" />}
                />
              </FormControl>
              <Button variant="outlined" onClick={fetchTrendData}>
                更新
              </Button>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* 推移グラフ */}
                {trendData.length > 0 && (
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                      達成率推移グラフ
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart
                        data={getChartData()}
                        margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="period" />
                        <YAxis
                          domain={[0, 'auto']}
                          tickFormatter={(value) => `${value}%`}
                        />
                        <Tooltip
                          formatter={(value: number, name: string) => {
                            if (name.includes('_rate')) {
                              return [`${value}%`, name.replace('_rate', '')];
                            }
                            return [formatCurrencyWithUnit(value), name.replace('_cum', ' 累積')];
                          }}
                        />
                        <Legend />
                        {trendData.map((emp, index) => (
                          <Line
                            key={emp.employee_id}
                            type="monotone"
                            dataKey={`${emp.employee_name}_rate`}
                            name={emp.employee_name}
                            stroke={colors[index % colors.length]}
                            strokeWidth={2}
                            dot={{ fill: colors[index % colors.length] }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Box>
                )}

                {/* 詳細テーブル */}
                <Typography variant="h6" sx={{ mb: 2 }}>推移詳細</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>担当者</TableCell>
                        <TableCell align="right">目標</TableCell>
                        {trendPeriods.slice(-8).map(period => (
                          <TableCell key={period} align="center">{period}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {trendData.length > 0 ? (
                        trendData.map((emp) => (
                          <TableRow key={emp.employee_id}>
                            <TableCell>{emp.employee_name}</TableCell>
                            <TableCell align="right">
                              {emp.has_target ? formatCurrencyWithUnit(emp.target_expected_sales) : '未設定'}
                            </TableCell>
                            {trendPeriods.slice(-8).map(period => {
                              const trendItem = emp.trend.find(t => t.period === period);
                              return (
                                <TableCell key={period} align="center">
                                  {trendItem ? (
                                    trendItem.achievement_rate !== null ? (
                                      <Chip
                                        label={`${trendItem.achievement_rate}%`}
                                        size="small"
                                        color={getAchievementColor(trendItem.achievement_rate)}
                                      />
                                    ) : (
                                      '-'
                                    )
                                  ) : (
                                    '-'
                                  )}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={10} align="center">
                            <Typography color="text.secondary">データがありません</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </TabPanel>
        </CardContent>
      </Card>

      {/* 目標設定ダイアログ */}
      <Dialog open={targetDialogOpen} onClose={() => setTargetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTarget?.employee_id ? '目標を編集' : '目標を設定'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>年度</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {fiscalYears.map((year) => (
                  <Chip
                    key={year}
                    label={`${year}年度`}
                    color={targetFiscalYear === year ? 'primary' : 'default'}
                    variant={targetFiscalYear === year ? 'filled' : 'outlined'}
                    onClick={() => setTargetFiscalYear(year)}
                    sx={{ cursor: 'pointer' }}
                  />
                ))}
              </Box>
            </Box>
            {!editingTarget?.employee_id ? (
              <Autocomplete
                options={Array.isArray(employees) ? employees : []}
                getOptionLabel={(option) => `${option.name}${option.department_name ? ` (${option.department_name})` : ''}`}
                value={(Array.isArray(employees) ? employees.find(e => e.employee_id === editingTarget?.employee_id) : null) || null}
                onChange={(_, newValue) => {
                  if (newValue && editingTarget) {
                    setEditingTarget({
                      ...editingTarget,
                      employee_id: newValue.employee_id,
                      employee_name: newValue.name,
                    });
                  }
                }}
                renderInput={(params) => <TextField {...params} label="担当者" required />}
              />
            ) : (
              <TextField
                label="担当者"
                value={editingTarget?.employee_name || ''}
                disabled
              />
            )}
            <TextField
              label="期待売上目標"
              type="number"
              value={editingTarget?.target_expected_sales || ''}
              onChange={(e) => setEditingTarget(prev => prev ? {
                ...prev,
                target_expected_sales: parseFloat(e.target.value) || 0
              } : null)}
              InputProps={{
                endAdornment: <Typography variant="body2" color="text.secondary">円</Typography>
              }}
              helperText="年間の期待売上目標額を入力してください"
            />
            <TextField
              label="粗利目標"
              type="number"
              value={editingTarget?.target_gross_profit || ''}
              onChange={(e) => setEditingTarget(prev => prev ? {
                ...prev,
                target_gross_profit: parseFloat(e.target.value) || 0
              } : null)}
              InputProps={{
                endAdornment: <Typography variant="body2" color="text.secondary">円</Typography>
              }}
              helperText="年間の受注粗利目標額を入力してください"
            />
            <TextField
              label="備考"
              multiline
              rows={2}
              value={editingTarget?.notes || ''}
              onChange={(e) => setEditingTarget(prev => prev ? {
                ...prev,
                notes: e.target.value
              } : null)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTargetDialogOpen(false)}>キャンセル</Button>
          <Button
            variant="contained"
            onClick={handleSaveTarget}
            disabled={savingTarget || !editingTarget?.employee_id}
          >
            {savingTarget ? <CircularProgress size={20} /> : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 内訳ダイアログ */}
      <Dialog
        open={breakdownOpen}
        onClose={() => setBreakdownOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {breakdownEmployee?.employee_name} — 期待売上内訳（{fiscalYear}年度）
        </DialogTitle>
        <DialogContent>
          {breakdownLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : breakdownDetails.length === 0 ? (
            <Alert severity="info" sx={{ mt: 1 }}>明細データがありません</Alert>
          ) : (
            <TableContainer sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>日付</TableCell>
                    <TableCell>案件番号</TableCell>
                    <TableCell>案件名</TableCell>
                    <TableCell>フェーズ</TableCell>
                    <TableCell>種別</TableCell>
                    <TableCell align="right">予定受注額</TableCell>
                    <TableCell align="right">貢献率</TableCell>
                    <TableCell align="right">期待売上額</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {breakdownDetails.map((d, i) => (
                    <TableRow
                      key={i}
                      hover
                      onClick={() => { setBreakdownOpen(false); navigate(`/sales/cases/${d.case_id}`); }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {formatDateJST(d.changed_at)}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{d.case_no}</TableCell>
                      <TableCell>{d.case_name}</TableCell>
                      <TableCell>
                        <Chip label={d.phase_name} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {d.change_type === 'initial' ? '初期登録' : d.change_type === 'phase_change' ? 'フェーズ変更' : d.change_type}
                      </TableCell>
                      <TableCell align="right">{formatCurrencyWithUnit(d.expected_order_amount)}</TableCell>
                      <TableCell align="right">{d.contribution_rate}%</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrencyWithUnit(d.contribution_amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' } }}>
                    <TableCell colSpan={7} align="right">合計</TableCell>
                    <TableCell align="right">
                      {formatCurrencyWithUnit(breakdownDetails.reduce((sum, d) => sum + d.contribution_amount, 0))}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBreakdownOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesKpiDashboardPage;
